import { useCallback, useEffect, useMemo } from "react";

import {
  usePaginationState,
  useSortState,
  useSelectionState,
  useSafeSetState,
} from "react-admin";
import { indexById } from "ra-core";

import { orderBy } from "lodash";

import { logError } from "./appUtils";

/**
 * Custom React hook for managing local list state with optional secondary sort logic.
 *
 * This is a generic alternative to `useList` from react-admin. It provides:
 * - Stable pagination, selection, and sorting logic
 * - Optional secondary field-based sort that complements the primary sort
 * - Performance optimization by avoiding unnecessary re-renders
 *
 * Recommended for scenarios where:
 * - You want to apply deterministic, multi-level sorting (e.g., productName then lotNumber)
 * - The data is managed entirely client-side (not paginated or sorted by the server)
 *
 * @param {Object} params
 * @param {Object[]} params.data - Full list of records to display
 * @param {string[]} params.ids - Ordered array of record identifiers
 * @param {boolean} params.loaded - Whether data has finished loading
 * @param {boolean} params.loading - Whether data is currently loading
 * @param {number} [params.page=1] - Initial page number
 * @param {number} [params.perPage=10] - Items per page
 * @param {{ field: string, order: 'ASC' | 'DESC' }} params.sort - Primary sort field and order
 * @param {{ field: string, order: 'ASC' | 'DESC' }} [params.secondarySort] - Optional secondary sort
 * @param {Function} [params.notify] - Optional error logger (e.g., react-admin's `useNotify`)
 *
 * @returns {{
 *   data: Record<string, any>,
 *   ids: string[],
 *   total: number,
 *   loaded: boolean,
 *   loading: boolean,
 *   page: number,
 *   perPage: number,
 *   setPage: (page: number) => void,
 *   setPerPage: (perPage: number) => void,
 *   currentSort: { field: string, order: 'ASC' | 'DESC' },
 *   setSort: (field: string, order: 'ASC' | 'DESC') => void,
 *   selectedIds: string[],
 *   onSelect: (ids: string[]) => void,
 *   onToggleItem: (id: string) => void,
 *   onUnselectItems: () => void,
 * }}
 *
 * @example
 * const listContext = useListWithSecondarySort({
 *   data: inventories,
 *   ids: inventories.map(i => i.id),
 *   loaded: true,
 *   loading: false,
 *   sort: { field: "productName", order: "ASC" },
 *   secondarySort: { field: "lotNumber", order: "ASC" }
 * });
 */
export const useListWithSecondarySort = ({
  data = [],
  ids = [],
  loaded,
  loading,
  page: initialPage = 1,
  perPage: initialPerPage = 10,
  sort: initialSort = defaultSort,
  secondarySort = null,
  notify,
}) => {
  // 1) build id→record map
  const dataMap = useMemo(() => indexById(data), [data]);

  // 2) pagination state
  const { page, perPage, setPage, setPerPage } = usePaginationState({
    page: initialPage,
    perPage: initialPerPage,
  });

  // 3) sort state
  const { sort, setSort: setSortInternal } = useSortState(initialSort);
  const setSort = useCallback(
    (field, order = "ASC") => {
      setSortInternal({ field, order });
      setPage(1);
    },
    [setSortInternal, setPage],
  );

  // 4) selection state (for bulk actions)
  const { selectedIds, onSelect, onToggleItem, onUnselectItems } =
    useSelectionState();

  // 5) final state
  const [finalState, setFinalState] = useSafeSetState({
    data: indexById(data),
    ids,
    total: ids.length,
  });

  // 6) whenever anything changes, re-compute sort & paginate
  useEffect(() => {
    if (!loaded) return;

    try {
      const sortFields = [];
      const sortOrders = [];

      if (sort.field) {
        sortFields.push(sort.field);
        sortOrders.push(sort.order?.toLowerCase() || "asc");
      }

      if (
        secondarySort &&
        secondarySort.field &&
        secondarySort.field !== sort.field // don't repeat the same field
      ) {
        sortFields.push(secondarySort.field);
        sortOrders.push(secondarySort.order?.toLowerCase() || "asc");
      }

      // pull out records in their original id order, then reorder:
      const records = ids.map((id) => dataMap[id]).filter(Boolean);
      const sorted = orderBy(records, sortFields, sortOrders);

      // paginate
      const total = sorted.length;

      // A client-side list can shrink under the reader: a filter toggled off,
      // rows picked away. The page they were on can stop existing, and slicing
      // past the end of the list hands back an empty page with no way out --
      // <Pagination> only rescues page < 1, never a page above the last one.
      // Walk them back to the last page that still holds rows. The slice uses
      // the clamped page in the same pass rather than waiting for the setPage
      // re-run, so the rows that just left the list never flash back for the
      // frame in between.
      const lastPage = Math.max(1, Math.ceil(total / perPage));
      const effectivePage = Math.min(page, lastPage);
      if (page > lastPage) {
        setPage(lastPage);
      }

      const paged = sorted.slice(
        (effectivePage - 1) * perPage,
        effectivePage * perPage,
      );
      const newIds = paged.map((r) => r.id);
      const newData = indexById(paged);

      // avoid infinite loops by shallow-comparing
      setFinalState((prev) =>
        prev.total === total &&
        prev.ids.length === newIds.length &&
        prev.ids.every((id, i) => id === newIds[i])
          ? prev
          : { data: newData, ids: newIds, total },
      );
    } catch (error) {
      logError("utils:useListWithSecondarySort", error, notify);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dataMap,
    ids,
    loaded,
    page,
    perPage,
    sort.field,
    sort.order,
    defaultSort.field,
    defaultSort.order,
    secondarySort,
    setFinalState,
    setPage,
  ]);

  // 7) return same shape as useList
  return {
    data: finalState.data,
    ids: finalState.ids,
    total: finalState.total,
    loaded,
    loading,
    page,
    perPage,
    setPage,
    setPerPage,
    currentSort: sort,
    setSort,
    selectedIds,
    onSelect,
    onToggleItem,
    onUnselectItems,
  };
};

const defaultSort = { field: null, order: null };
